import { useCallback, useEffect, useState, useRef } from "react";
import { notifyError, notifySuccess } from "src/assets/toastService";
import { useUser } from "src/shared/context/UserContext";
import api from "src/features/auth/axiosConfig";
import { errorHandling } from "src/shared/utils/errorHandling";

interface OpenPayload {
  businessDate: Date;
  openingCash: number;
  notes: string;
  shiftType?: string;
  operationType: "Day" | "Shift";
}

interface ClosePayload {
  closingCash: number;
}

export const useBusinessOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userConfigration } = useUser();
  const operationType = userConfigration?.businessOperationType || "Day";
  const [currentStatus, setCurrentStatus] = useState<"OPEN" | "CLOSED" | null>(null);

  // Add ref to track if we're currently fetching
  const isFetchingRef = useRef(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();

  // ======================================================
  // FETCH CURRENT OPEN OPERATION FOR TODAY
  // ======================================================
  // In useBusinessOperations.ts, modify the getCurrentOperation function:

  const getCurrentOperation = useCallback(async () => {
    if (isFetchingRef.current) {
      return currentStatus;
    }

    try {
      isFetchingRef.current = true;

      const response = await api.get(`/app/pos/v2/business_operations?status=OPEN`);

      const operations = response?.data?.result || [];

      // Get today's date
      const now = new Date();

      // Find any OPEN operation from TODAY
      const todayOpenOperation = operations.find((op: any) => op.status === "OPEN");

      if (todayOpenOperation) {
        setCurrentStatus("OPEN");
        return "OPEN";
      } else {
        setCurrentStatus("CLOSED");
        return "CLOSED";
      }
    } catch (err) {
      console.error("Error fetching current operation:", err);
      setCurrentStatus(null);
      return null;
    } finally {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
      fetchTimeoutRef.current = setTimeout(() => {
        isFetchingRef.current = false;
      }, 500);
    }
  }, []);

  // Initial fetch only once
  useEffect(() => {
    getCurrentOperation();

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, []); // Empty dependency array - only run once

  // ================= OPEN DAY (POST) =================
  const openDay = async ({ businessDate, openingCash, notes, shiftType }: OpenPayload) => {
    setLoading(true);
    setError(null);
    const localDate = businessDate.getFullYear() + "-" + String(businessDate.getMonth() + 1).padStart(2, "0") + "-" + String(businessDate.getDate()).padStart(2, "0");

    try {
      const payload = {
        businessDay: localDate,
        type: operationType === "Shift" ? "shift" : "day",
        shift_type: operationType === "Shift" ? shiftType : "no shift",
        openingCash,
        notes,
      };

      const response = await api.post("/app/pos/v2/business_operations", payload);
      const successMessage = operationType === "Shift" ? `Shift opened successfully${shiftType ? ` (${shiftType})` : ""}` : "Business day opened successfully";

      notifySuccess(successMessage);

      // Reset fetching flag and update status
      isFetchingRef.current = false;
      setCurrentStatus("OPEN");

      return response.data;
    } catch (err: any) {
      const message = err.response?.data?.message || "Failed to open business day";
      setError(message);
      errorHandling(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ================= GET SUMMARY (GET) =================
  const getBusinessSummary = async (id: string) => {
    setLoading(true);
    setError(null);

    if(!id){
        return
    }else{
        
        try {
          const response = await api.get(`/app/pos/v2/business_operations/${id}`);
          return response.data;
        } catch (err: any) {
          console.error("API error:", err);
          const message = err.response?.data?.message || "Failed to fetch business summary";
          notifyError(message);
          setError(message);
          errorHandling(err);
          throw err;
        } finally {
          setLoading(false);
        }
    }
  };

  // ================= CLOSE DAY (PUT) =================
  const closeDay = async (id: string, { closingCash }: ClosePayload) => {
    setLoading(true);
    setError(null);

    try {
      const payload = {
        closingCash,
      };

      const response = await api.put(`/app/pos/v2/business_operations/${id}`, payload);
      notifySuccess("Business day closed successfully");

      // Reset fetching flag and update status
      isFetchingRef.current = false;
      setCurrentStatus("CLOSED");

      return response.data;
    } catch (err: any) {
      const message = err.response?.data?.message || "Failed to close business day";
      setError(message);
      errorHandling(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = useCallback(async () => {
    // Clear any pending timeout
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
    }

    // Reset fetching flag to allow new fetch
    isFetchingRef.current = false;

    return await getCurrentOperation();
  }, [getCurrentOperation]);

  return {
    openDay,
    closeDay,
    getBusinessSummary,
    loading,
    error,
    currentStatus,
    refreshStatus,
  };
};
